/**
 * SITE-OVERVIEW.TSX
 * Current site dashboard
 */
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import siteStore from "../model/site.store"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import { useEffect, useRef } from "react"
import PhysicalRiskCard from "@/entities/physical-risk/ui/PhysicalRiskCard"
import useOpenPhysicalRiskFromQuery from "@/entities/physical-risk/hooks/useOpenPhysicalRiskFromQuery"
import DashboardNumber from "@/components/dashboard-number"
import { PhysicalRiskMeter } from "@/entities/physical-risk/ui/PhysicalRiskMeter"
import { useNavigate } from "react-router-dom"
import { ZoomIn } from "@/utils/animations.utils"
import MapContainer from "@/entities/map/ui/MapContainer"
import populationStore from "@/entities/population/model/population.store"
import PopulationCard from "@/entities/population/ui/PopulationCard"
import PageHeader from "@/components/page-header"
import { useRisksSpace } from "@/core/risks-space.context"

export default function SiteOverview() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { getPath } = useRisksSpace()
  const overviewRef = useRef<HTMLDivElement>(null)

  //Selectors
  const activeSite = useAppSelector(siteStore.selectors.active)
  const risksForSite = useAppSelector(physicalRiskStore.selectors.listForSite)
  const sitePopulations = useAppSelector(populationStore.selectors.listForSite)
  const isIdtreeScoreHidden = useAppSelector((state) => state._session.accountRisksSettings.isIdtreeScoreHidden)

  useOpenPhysicalRiskFromQuery(risksForSite)

  useEffect(() => {
    overviewRef.current?.scrollTo({ top: 0 })
  }, [activeSite.id])

  //If no site
  if (activeSite.EstablishmentId)
    return (
      <ZoomIn ref={overviewRef} className="lightwhite box-border h-full overflow-y-auto px-40 pb-80 pt-20">
        <div
          className={`overview-header grid ${activeSite.MapId ? "grid-cols-[minmax(0,1fr)_220px]" : "grid-cols-1"} items-stretch gap-40`}>
          <div>
            <PageHeader section="Risques" title={activeSite.name} />

            <div className="flex items-center">
              {activeSite.cRiskMax && (
                <div className="flex">
                  <div className="my-auto mr-20">
                    <PhysicalRiskMeter risk={activeSite.cRiskMax} size="lg" />
                  </div>
                  {!isIdtreeScoreHidden && (
                    <div className="w-[150px]">
                      <DashboardNumber
                        color={activeSite.cColor}
                        count={activeSite.cRiskMax.cValueEffectiveLabel}
                        header="Risque résiduel"
                        fontSize={18}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {activeSite.MapId && (
            <div className="flex justify-start">
              <div
                className="flex h-full min-h-[132px] w-[220px] overflow-hidden _hover bg-white"
                style={{ borderRadius: 12 }}
                onClick={() => {
                  dispatch(siteStore.actions.setActive(null))
                  dispatch(physicalRiskStore.actions.setActive(null))
                  navigate(getPath("/sites"))
                }}>
                <MapContainer selectedSite={activeSite} />
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap">
          {risksForSite.map((risk) => {
            return <PhysicalRiskCard key={risk.id} risk={risk} />
          })}
        </div>

        {sitePopulations.length > 0 && (
          <div className="grey-t mt-40 mb-20">
            <b>Fonctions</b>
          </div>
        )}

        <div className="flex flex-wrap">
          {sitePopulations.map((population) => (
            <PopulationCard key={population.id} population={population} />
          ))}
        </div>
      </ZoomIn>
    )
}
